import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "../../styles/AddressForm.css";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import CustomButton from "../../component/ui/FormFields/Button";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ControlledRadioButtonsGroup from "../../component/ui/FormFields/ControlledRadioButtonsGroup";
import {
  addFamilyMemberRequest,
  resetFamilyMember,
} from "../../redux/FamilyMembers/actions";
import { selectCountryRequest } from "../../redux/Common/selectorAction";
import { convertDateFormat, convertDateFormatBooking } from "../../Style";

export default function BookingAddFamilyMember({
  customerNo,
  addFamilyMemberModalClose,
  isInternational = false,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
    clearErrors,
    trigger,
  } = useForm();
  const formValue = watch();
  const customerId = customerNo;

  const countries = useSelector((state) => state.selector?.country?.data);
  const loading = useSelector((state) => state.familyMember.loading);
  const error = useSelector((state) => state.familyMember.error);
  const success = useSelector((state) => state.familyMember.success);

  const countryForCode = countries?.map((value) => {
    return {
      label: ` +${value.phonecode} - ${value.short_name}`,
      value: value.phonecode,
    };
  });

  const passportNumber = watch("passport_number");
  const aadharNumber = watch("aadhar_number");
  const dateOfBirth = watch("dob");

  const calculateAge = (dob) => {
    if (!dob) return 0;

    let birthDate;

    // Handle different date formats
    if (typeof dob === "string") {
      // If format is DD-MM-YYYY or DD/MM/YYYY
      if (dob.includes("-") || dob.includes("/")) {
        const separator = dob.includes("-") ? "-" : "/";
        const parts = dob.split(separator);

        if (parts.length === 3) {
          // Assuming DD-MM-YYYY or DD/MM/YYYY format
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);

          birthDate = new Date(year, month, day);
        } else {
          birthDate = new Date(dob);
        }
      } else {
        birthDate = new Date(dob);
      }
    } else {
      birthDate = new Date(dob);
    }

    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
      return 0;
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const isAdult = calculateAge(dateOfBirth) > 12;

  useEffect(() => {
    if (passportNumber || aadharNumber) {
      clearErrors(["passport_number", "aadhar_number"]);
    }
  }, [passportNumber, aadharNumber, clearErrors, trigger]);

  useEffect(() => {
    if (!isAdult) {
      clearErrors(["phone", "country_code"]);
    }
  }, [isAdult, clearErrors]);

  const validateMobileNumber = (value) => {
    if (isAdult) {
      if (!value || value.trim() === "") {
        return "Mobile No is required";
      }
      if (!/^\d+$/.test(value)) {
        return "Enter only numeric digits";
      }
    }

    if (value && !/^\d+$/.test(value)) {
      return "Enter only numeric digits";
    }
    return true;
  };

  const validateCountryCode = (value) => {
    if (isAdult) {
      return value || "Country Code is required";
    }
    return true;
  };

  const passengerDetails = [
    {
      name: "first_name",
      label: "First Name *",
      placeholder: "Enter first name",
      rules: { required: "First Name is required" },
      xs: 12,
      sm: 6,
    },
    {
      name: "last_name",
      label: "Last Name *",
      placeholder: "Enter last name",
      rules: { required: "Last Name is required" },
      xs: 12,
      sm: 6,
    },
    {
      name: "dob",
      label: "Date of Birth *",
      placeholder: "Select date of birth",
      type: "dob",
      rules: { required: "Date of Birth is required" },
      xs: 12,
      sm: 6,
    },
    ...(isInternational
      ? [
          {
            name: "passport_number",
            label: "Passport Number",
            placeholder: "Enter Passport Number",
            type: "Text",
            rules: {
              required: "Passport is required for international flights",
              validate: {
                passportFormat: (value) => {
                  if (!value || value.trim() === "") {
                    return "Passport is required for international flights";
                  }

                  const patterns = {
                    US: /^[0-9]{9}$/,
                    UK: /^[0-9]{9}$|^[A-Z]{2}[0-9]{7}$/,
                    EU: /^[A-Z]{1,2}[0-9]{5,7}$/,
                    CA: /^[A-Z]{2}[0-9]{6}$/,
                    AU: /^[A-Z][0-9]{8}$/,
                    IN: /^[A-Z][0-9]{7}$/,
                    general: /^[A-Z0-9]{5,12}$/i,
                  };

                  return (
                    patterns.US.test(value) ||
                    patterns.UK.test(value) ||
                    patterns.EU.test(value) ||
                    patterns.CA.test(value) ||
                    patterns.AU.test(value) ||
                    patterns.IN.test(value) ||
                    patterns.general.test(value) ||
                    "Please enter a valid passport number"
                  );
                },
              },
            },
            xs: 12,
            sm: 6,
          },
        ]
      : []),
    // {
    //   name: "aadhar_number",
    //   label: "Aadhar Number",
    //   placeholder: "Enter Aadhar Number",
    //   type: "Number",
    //   rules: {
    //     validate: {
    //       aadharFormat: (value) => {
    //         if (!value || value.trim() === "") return true;

    //         return (
    //           /^\d{12}$/.test(value) ||
    //           "Aadhar Number must be exactly 12 digits"
    //         );
    //       },
    //     },
    //   },
    //   xs: 12,
    //   sm: 6,
    // },
    {
      name: "nationality",
      label: "Nationality *",
      placeholder: "Enter Nationality",
      type: "text",
      rules: {
        required: "Nationality is required",
        pattern: {
          value: /^[A-Za-z\s]+$/,
          message: "Only alphabets are allowed",
        },
      },
      xs: 12,
      sm: 6,
      inputProps: {
        onInput: (e) => {
          e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
        },
      },
    },
    {
      name: "country_code",
      label: isAdult ? "Country Code *" : "Country Code",
      placeholder: "Enter Country Code",
      options: countryForCode,
      rules: {
        validate: validateCountryCode,
      },
      xs: 4,
      sm: 2,
    },
    {
      name: "phone",
      label: isAdult ? "Mobile No *" : "Mobile No",
      placeholder: "Enter mobile number",
      type: "Number",
      rules: {
        validate: validateMobileNumber,
      },
      xs: 8,
      sm: 4,
    },
    {
      name: "gender_type",
      label: "Gender *",
      type: "radio",
      options: [
        { value: "M", label: "Male" },
        { value: "F", label: "Female" },
        { value: "O", label: "Other" },
      ],
      rules: { required: "Select Gender" },
      xs: 12,
      sm: 6,
    },
  ];

  useEffect(() => {
    dispatch(selectCountryRequest());
    dispatch(resetFamilyMember());
  }, []);

  const onSubmit = (data) => {
    const currentAge = calculateAge(data.dob);
    if (currentAge > 12) {
      if (!data.phone || data.phone.trim() === "") {
        setError("phone", {
          type: "manual",
          message: "Mobile No is required for adults",
        });
        return;
      }
      if (!data.country_code) {
        setError("country_code", {
          type: "manual",
          message: "Country Code is required for adults",
        });
        return;
      }
    }

    const formData = {
      ...data,
      country_code: data?.country_code?.value || data?.country_code,
      customer_id: customerId,
      type: "1",
      dob: convertDateFormatBooking(data?.dob),
    };
    dispatch(addFamilyMemberRequest(formData));
  };

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-center",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: false,
      });
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      toast.success("Family member added successfully", {
        position: "top-center",
        autoClose: 3000,
        closeOnClick: true,
        onClose: () => {
          dispatch(resetFamilyMember());
          addFamilyMemberModalClose();
        },
        pauseOnHover: false,
      });
    }
  }, [success, dispatch, addFamilyMemberModalClose]);

  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card className="form-card">
          <CardContent>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              className="form-heading"
            >
              Add Passenger Detail
            </Typography>
            <Divider className="form-divider" />
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {passengerDetails.map((field, index) => (
                  <Grid item xs={field.xs} sm={field.sm} key={index}>
                    {field.type === "radio" ? (
                      <ControlledRadioButtonsGroup
                        name={field.name}
                        control={control}
                        label={field.label}
                        rules={field.rules}
                        options={field.options || field.radioOptions}
                        errors={errors}
                      />
                    ) : (
                      <ControlledTextInputField
                        name={field.name}
                        control={control}
                        label={field.label}
                        placeholder={field.placeholder}
                        rules={field.rules}
                        options={field.options}
                        type={field.type}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]?.message}
                        inputProps={field.inputProps}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Box className="button-container">
                  <CustomButton
                    variant="contained"
                    className="cancel-button"
                    type="button"
                    text={"Cancel"}
                    onClick={() => addFamilyMemberModalClose()}
                  />
                  <CustomButton
                    variant="contained"
                    className="success-button"
                    loading={loading}
                    type="button"
                    text={"Submit"}
                    onClick={handleSubmit(onSubmit)}
                  />
                </Box>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
